import { IconButton, Tab, Tabs } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import React, { ReactNode, useRef } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import FixedTab from './FixedTab';
import useStyles from './styles';
import VariableTab from './VariableTab';
import cx from 'classnames';

interface IRouteTabsProps {
  path: string;
  isNested?: boolean;
  minWidth?: number;
  children: JSX.Element[];
  className?: string;
}

const RouteTabs = (props: IRouteTabsProps): JSX.Element => {
  const { isNested = false, minWidth, children, path } = props;

  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const visitedTabs = useRef<Set<string>>(new Set<string>());

  const getLinkTo = (uri: string | undefined): string => {
    const to = uri ? `${path}/${uri.charAt(0) === '/' ? uri.slice(1) : uri}` : path;

    return to;
  };

  const getCurrentValueByPathname = (pathname: string): string | null => {
    const linkToArray: string[] = [];

    React.Children.forEach(children, child => {
      if (!child || !child.props || !child.props.label) {
        return;
      }

      if (child.type === VariableTab) {
        const { data } = child.props;

        data.forEach((d: any) => {
          const uri = child.props.uri(d);
          linkToArray.push(getLinkTo(uri));
        });
      } else if (child.type === FixedTab) {
        linkToArray.push(getLinkTo(child.props.uri));
      } else {
        console.warn('Children other than VariableTab or FixedTab components are ignored and not rendered.');
        return;
      }
    });

    if (!pathname) {
      return linkToArray[0]; // default
    }

    if (linkToArray.findIndex((linkTo: string) => linkTo === pathname) >= 0) {
      return pathname;
    }

    const splitted = pathname.split('/');
    splitted.pop();
    const newPathname = splitted.join('/');

    return getCurrentValueByPathname(newPathname);
  };

  const renderTabs = () => {
    const tabElements: JSX.Element[] = [];

    React.Children.forEach(children, child => {
      if (!child || !child.props || !child.props.label) {
        return;
      }

      if (child.type === VariableTab) {
        const { data, keyExtractor } = child.props;

        data.forEach((d: any) => {
          const { onClose } = child.props;
          const label: string | JSX.Element = child.props.label(d);
          const uri: string = child.props.uri(d);

          const to = getLinkTo(uri);

          tabElements.push(
            <Tab
              component={Link}
              value={to}
              to={to}
              label={
                <div className={classes.labelWrap}>
                  <span className={classes.label}>{label}</span>
                  <IconButton
                    size="small"
                    edge="start"
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();

                      visitedTabs.current.delete(to);

                      const currentTabIndex = tabElements.findIndex(t => t.props.to === to);
                      const toBeSelectedTabAfterClose = tabElements[0].props.to;

                      // 탭 데이터를 지우는 것은 위임
                      onClose(d); // pass data

                      if (toBeSelectedTabAfterClose && location.pathname === to) {
                        history.replace(toBeSelectedTabAfterClose);
                      }

                      return false;
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </div>
              }
              wrapped
              style={{ textTransform: 'none' }}
              key={keyExtractor(d)}
              classes={{
                root: isNested ? classes.nestedTabRoot : undefined,
                wrapper: isNested ? classes.nestedTabWrapper : undefined,
              }}
              disableRipple={isNested}
            />,
          );
        });
      } else if (child.type === FixedTab) {
        const { label, uri } = child.props;
        const to = getLinkTo(uri);

        tabElements.push(
          <Tab
            component={Link}
            value={to}
            to={to}
            label={label}
            wrapped
            style={{ textTransform: 'none' }}
            key={`tab-${label}-${uri || ''}`}
            classes={{
              root: isNested ? classes.nestedTabRoot : undefined,
              wrapper: isNested ? classes.nestedTabWrapper : undefined,
            }}
            disableRipple={isNested}
          />,
        );
      } else {
        console.warn('Children other than VariableTab or FixedTab components are ignored and not rendered.');
        return;
      }
    });

    return tabElements;
  };

  const renderChildren = () => {
    const childrenToRender: ReactNode[] = [];

    React.Children.forEach(children, child => {
      if (!child || !child.props || !child.props.label) {
        return;
      }

      if (child.type === VariableTab) {
        const { data, keyExtractor } = child.props;

        data.forEach((d: any) => {
          const uri = child.props.uri(d);
          const index = getLinkTo(uri);
          const value = getCurrentValueByPathname(location.pathname);

          const render = child.props.render;

          const isAlreadyVisitedTab = visitedTabs.current.has(index);

          if (value === index || isAlreadyVisitedTab) {
            if (!isAlreadyVisitedTab) {
              visitedTabs.current.add(index);
            }

            childrenToRender.push(
              React.cloneElement(child, {
                value,
                index,
                key: keyExtractor(d),
                children: render(d),
              }),
            );
          } else {
            childrenToRender.push(null);
          }
        });
      } else if (child.type === FixedTab) {
        const { uri } = child.props;
        const index = getLinkTo(uri);
        const value = getCurrentValueByPathname(location.pathname);

        childrenToRender.push(cloneElement(child, value, index));
      } else {
        console.warn('Children other than VariableTab or FixedTab components are ignored and not rendered.');
        return;
      }
    });

    return childrenToRender;
  };

  const cloneElement = (child: any, value: string | null, index: string): ReactNode => {
    if (value === null) {
      return null;
    }

    const isAlreadyVisitedTab = visitedTabs.current.has(index);

    if (value === index || isAlreadyVisitedTab) {
      if (!isAlreadyVisitedTab) {
        visitedTabs.current.add(index);
      }

      return React.cloneElement(child, {
        key: `tab-fixed-${index}`,
        value,
        index,
      });
    } else {
      return null;
    }
  };

  const currentValue = getCurrentValueByPathname(location.pathname);

  return (
    <div className={cx(classes.root, props.className)} style={minWidth ? { minWidth } : undefined}>
      <Tabs
        value={currentValue}
        indicatorColor={isNested ? 'primary' : undefined}
        textColor={isNested ? 'primary' : undefined}
        TabIndicatorProps={
          isNested
            ? {
                className: classes.tabIndicator,
              }
            : undefined
        }
        classes={{
          root: isNested ? classes.nestedTabsRoot : classes.tabsRoot,
        }}
        // variant="scrollable"
        // scrollButtons="auto"
      >
        {renderTabs()}
      </Tabs>
      {renderChildren()}
    </div>
  );
};

export default RouteTabs;
